import React, { useEffect } from "react";
import { Button } from 'primereact/button';
import { RadioButton } from "primereact/radiobutton";
import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from "primereact/inputtext";
import { useFormik } from 'formik';
import { classNames } from 'primereact/utils';
import { Calendar } from 'primereact/calendar';
import { ProgressSpinner } from 'primereact/progressspinner';
import {
    Form,
    redirect,
    useActionData,
    useLoaderData,
    useNavigate,
    useSubmit,
    useNavigation
} from "react-router-dom";
import { getClient, createClient, updateClient } from "../../../data/loca/clients";
import { send, notify } from "../../../services/NotificationService";
import { INVALID_PHONE_MESSAGE, isPhoneTooLong } from "../../../services/phone";

export async function action({ request, params }) {

    const formData = await request.formData();
    const data = Object.fromEntries(formData);

    // Champ refusé par l'API : on reste sur le formulaire, le composant affiche la notification.
    let response;
    try {
        if (params.clientId)
            response = await updateClient(data, params.clientId);
        else
            response = await createClient(data);
    } catch (error) {
        if (error instanceof Response)
            throw error;
        return { error: error?.message || "Une erreur est survenue lors de l'enregistrement du client." };
    }

    if (response.error != null) {
        return null;
    } else {
        const clientId = params.clientId || response.publicId || response.id;
        if (clientId) {
            return redirect(`/clients/${clientId}`);
        }
        return redirect(`/clients/liste`);
    }
}
export async function loader({ params }) {
   
    if (params.clientId) {
        const client = await getClient(params.clientId);
        return { client };
    }

    const client = null;
    return { client };
}
export default function ClientForm() {
    const { client } = useLoaderData();
    const submit = useSubmit();
    const navigate = useNavigate();
    const navigation = useNavigation();
    const isSubmitting = navigation.state === "submitting";
    const actionData = useActionData();

    useEffect(() => {
        if (!actionData?.error) {
            return;
        }
        send("warning", actionData.error);
        notify();
    }, [actionData]);

    let email, firstname, lastname, phone, address, birthdate, birthplace, comment, clientType, companyName, siret;
    if (client) {
        email = client.email;
        firstname = client.firstname;
        lastname = client.lastname;
        phone = client.phone;
        address = client.address;
        birthdate = (client.birthdate) ? new Date(client.birthdate) : null;
        birthplace = client.birthplace;
        comment = client.comment;
        clientType = client.clientType;
        companyName = client.companyName;
        siret = client.siret;
    }
    // Formik ---------------------
 
    const formik = useFormik({
        initialValues: {
            email: email || "",
            firstname: firstname || "",
            lastname: lastname || "",
            phone: phone || "",
            address: address || "",
            birthdate: birthdate || "",
            birthplace: birthplace || "",
            comment: comment || "",
            clientType: clientType || "Private",
            companyName: companyName || "",
            siret: siret || "",
        },
        validate: (data) => {
            let errors = {};
            if (!data.email) {
                errors.email = "Email requis";
            }
            if (!data.firstname) {
                errors.firstname = "Prénom requis";
            }
            if (!data.lastname) {
                errors.lastname = "Nom requis";
            }
            if (!String(data.phone ?? "").trim()) {
                errors.phone = "Téléphone requis";
            } else if (isPhoneTooLong(data.phone)) {
                errors.phone = INVALID_PHONE_MESSAGE;
            }
            if (data.clientType === "Professional" && !data.companyName) {
                errors.companyName = "Nom de société requis";
            }


            return errors;
        },
        onSubmit: async (data) => {

            let error = false

            if (!error) { 

                submit(data, { method: "post" })
            }
        }
    });

    const isFormFieldInvalid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const isProfessionalClient = formik.values.clientType === "Professional";
    const pageTitle = `${client ? "Modification" : "Ajout"} d'un client`;
    const clientName = [formik.values.firstname, formik.values.lastname].filter(Boolean).join(" ") || "Nouveau client";

    //----------------------------------------

    return (
        <div className="account-page client-form-page">
            <div className="account-page-header">
                <div>
                    <p className="account-page-kicker">Clients</p>
                    <h1>{pageTitle}</h1>
                    <p>{clientName}</p>
                </div>
                <div className="account-status-card">
                    <span>Type de client</span>
                    <b>{isProfessionalClient ? "Professionnel" : "Particulier"}</b>
                </div>
            </div>

            <Form method="post" id="thumbnail-form" enctype="multipart/form-data" className="account-form client-form" onSubmit={formik.handleSubmit}>
                <section className="account-section">
                    <div className="account-section-header">
                        <i className="pi pi-users" aria-hidden="true"></i>
                        <div>
                            <h2>Type de client</h2>
                            <p>Déterminez les informations à collecter pour vos documents de location.</p>
                        </div>
                    </div>

                    <div className="account-choice-grid">
                        <label className={classNames('account-choice', { 'account-choice--selected': !isProfessionalClient })} htmlFor="client-type-private">
                            <RadioButton inputId="client-type-private" name="clientType" value="Private" onChange={(e) => {
                                formik.setFieldValue('clientType', e.value);
                                formik.setFieldValue('companyName', '');
                                formik.setFieldValue('siret', '');
                            }} checked={formik.values.clientType === 'Private'} />
                            <span>
                                <b>Particulier</b>
                                <small>Client personne physique avec identité et naissance optionnelle.</small>
                            </span>
                        </label>

                        <label className={classNames('account-choice', { 'account-choice--selected': isProfessionalClient })} htmlFor="client-type-professional">
                            <RadioButton inputId="client-type-professional" name="clientType" value="Professional" onChange={(e) => {
                                formik.setFieldValue('clientType', e.value);
                                formik.setFieldValue('birthdate', "");
                                formik.setFieldValue('birthplace', "");
                            }} checked={formik.values.clientType === 'Professional'} />
                            <span>
                                <b>Professionnel</b>
                                <small>Entreprise ou association avec informations administratives.</small>
                            </span>
                        </label>
                    </div>

                    {isProfessionalClient && (
                        <div className="account-fields-grid account-fields-grid--two client-form-company">
                            <span className="p-float-label">
                                <InputText id="companyName" name="companyName" value={formik.values.companyName}
                                    onChange={(e) => formik.setFieldValue('companyName', e.target.value)}
                                    className={classNames({ 'p-invalid': isFormFieldInvalid('companyName') })}
                                />
                                <label htmlFor="companyName">Nom de la soci&eacute;t&eacute;</label>
                                {isFormFieldInvalid('companyName') && <small className="p-error">{formik.errors.companyName}</small>}
                            </span>
                            <span className="p-float-label">
                                <InputText id="siret" name="siret" value={formik.values.siret}
                                    onChange={(e) => formik.setFieldValue('siret', e.target.value)}
                                />
                                <label htmlFor="siret">SIRET (optionnel)</label>
                            </span>
                        </div>
                    )}
                </section>

                <section className="account-section">
                    <div className="account-section-header">
                        <i className="pi pi-user" aria-hidden="true"></i>
                        <div>
                            <h2>Identité</h2>
                            <p>Nom du client repris dans vos contrats, devis et historiques.</p>
                        </div>
                    </div>
                    <div className="account-fields-grid account-fields-grid--two">
                        <span className="p-float-label">
                            <InputText id="firstname" name="firstname" value={formik.values.firstname}
                                data-cy="firstname"
                                onChange={(e) => {
                                    formik.setFieldValue('firstname', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('firstname') })}
                            />
                            <label htmlFor="firstname">Pr&eacute;nom</label>
                            {isFormFieldInvalid('firstname') && <small className="p-error">{formik.errors.firstname}</small>}
                        </span>
                        <span className="p-float-label">
                            <InputText id="lastname" name="lastname" value={formik.values.lastname} data-cy="lastname"
                                onChange={(e) => {
                                    formik.setFieldValue('lastname', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('lastname') })}
                            />
                            <label htmlFor="lastname">Nom</label>
                            {isFormFieldInvalid('lastname') && <small className="p-error">{formik.errors.lastname}</small>}
                        </span>
                    </div>
                    {!isProfessionalClient && (
                        <div className="account-fields-grid account-fields-grid--two client-form-birth">
                            <span className="p-float-label">
                                <Calendar id="birthdate" name="birthdate" value={formik.values.birthdate} data-cy="birthdate"
                                    dateFormat="dd/mm/yy"
                                    onChange={(e) => {
                                        formik.setFieldValue('birthdate', e.target.value);
                                    }}
                                    className={classNames({ 'p-invalid': isFormFieldInvalid('birthdate') })}
                                />
                                <label htmlFor="birthdate">N&eacute;(e) le</label>
                            </span>
                            <span className="p-float-label">
                                <InputText id="birthplace" name="birthplace" value={formik.values.birthplace} data-cy="birthplace"
                                    onChange={(e) => {
                                        formik.setFieldValue('birthplace', e.target.value);
                                    }}
                                    className={classNames({ 'p-invalid': isFormFieldInvalid('birthplace') })}
                                />
                                <label htmlFor="birthplace">Lieu de naissance</label>
                            </span>
                        </div >
                    )}
                </section>

                <section className="account-section">
                    <div className="account-section-header">
                        <i className="pi pi-map-marker" aria-hidden="true"></i>
                        <div>
                            <h2>Coordonnées</h2>
                            <p>Informations utiles pour contacter le client et préparer la location.</p>
                        </div>
                    </div>
                    <div className="account-fields-grid account-fields-grid--three">
                        <span className="p-float-label">
                            <InputText id="phone" name="phone" value={formik.values.phone} data-cy="phone"
                                onChange={(e) => {
                                    formik.setFieldValue('phone', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('phone') })}
                            />
                            <label htmlFor="phone">T&eacute;l&eacute;phone</label>
                            {isFormFieldInvalid('phone') && <small className="p-error">{formik.errors.phone}</small>}
                        </span>
                        <span className="p-float-label">
                            <InputText id="email" name="email" value={formik.values.email} data-cy="email"
                                onChange={(e) => {
                                    formik.setFieldValue('email', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('email') })}
                            />
                            <label htmlFor="email">Email</label>
                            {isFormFieldInvalid('email') && <small className="p-error">{formik.errors.email}</small>}
                        </span>
                        <span className="p-float-label account-field-wide">
                            <InputText id="address" name="address" value={formik.values.address} data-cy="address"
                                onChange={(e) => {
                                    formik.setFieldValue('address', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('address') })}
                            />
                            <label htmlFor="address">Adresse</label>
                        </span>
                    </div >
                </section>

                <section className="account-section">
                    <div className="account-section-header">
                        <i className="pi pi-comment" aria-hidden="true"></i>
                        <div>
                            <h2>Commentaires</h2>
                            <p>Notes internes visibles uniquement dans votre espace de gestion.</p>
                        </div>
                    </div>
                    <div className="account-fields-grid">
                        <InputTextarea id="comment" name="comment" value={formik.values.comment} data-cy="comment"
                            onChange={(e) => {
                                formik.setFieldValue('comment', e.target.value);
                            }}
                            rows={5}
                            className={classNames('client-form-comment', { 'p-invalid': isFormFieldInvalid('comment') })}
                        />
                    </div>
                </section>

                <div className="account-submit-bar">
                    <span>{isSubmitting ? "Enregistrement du client en cours..." : "Les informations seront utilisées sur les prochaines locations."}</span>
                    <div className="client-form-submit-actions">
                        {isSubmitting && <ProgressSpinner style={{ width: "24px", height: "24px" }} strokeWidth="4" />}
                        <Button type="button"
                            size="small"
                            severity="secondary"
                            outlined
                            onClick={() => {
                                navigate(-1);
                            }}>Annuler</Button>
                        <Button type="submit" icon="pi pi-save" size="small" data-cy="saveBtn" disabled={isSubmitting}>
                            {isSubmitting ? "Enregistrement..." : "Enregistrer"}
                        </Button>
                    </div>
                </div>
            </Form>
            </div>
    );
}
